import * as marietta from '../..';
import * as marietta3d from '../../../marietta3d';
import { Stage } from '../model';
import { OVERLAP_HITTEST_RADIUS_MM } from '../Constants';

/**
 * 重なっているアイテムリストの表示・操作クラス
 */
export default class OverlapItemList {
  private isPC = marietta.webcad.WebCADGlobal.userAgentManager.isPC;
  private itemList: marietta.webcad.item.MonicaObject[] = [];
  private wallItemList: marietta.webcad.item.parts.Wall[] = []; // 部屋壁リスト
  private readonly className: string = 'item-list'; // 共通クラス名
  private readonly attachedName: string = 'room-attached'; // 部屋に付属する床・天井名
  private unnecessaryItem = ['sit01_01', 'rof01_01', 'col01_01', 'fur26_01']; // リストに不要なアイテム
  private selectItemId: string = ''; // 選択中のアイテムID(マウスオーバー、ipadのみ)
  private isAttachedItem: boolean = false; // 部屋に付属する床・天井か(ipadのみ)

  /**
   * 重なっているアイテムリストの表示
   */
  public showList() {
    this.hideList();
    this.selectItemId = '';
    this.isAttachedItem = false;
    this.isPC = marietta.webcad.WebCADGlobal.userAgentManager.isPC;
    this.itemList = this.getOverlapItemList();
    this.wallItemList = this.getOverlapWallList();
    // 重なっているアイテムが無い場合はリスト表示しない
    if (this.itemList.length <= 1 && this.wallItemList.length <= 1) {
      this.itemList = [];
      this.wallItemList = [];
      return;
    }
    this.sortList();
    this.createList();
    this.addEvent();
  }

  /**
   * 重なったアイテムリストを非表示
   */
  public hideList() {
    this.removeList();
    const container = <HTMLElement>document.getElementById('overlapItemListContainer');
    container.style.display = 'none';
    this.itemList = [];
    this.wallItemList = [];
  }

  /**
   * アイテム長押し後、重なっているアイテム一覧の取得
   */
  private getOverlapItemList(): marietta.webcad.item.MonicaObject[] {
    const { allItems, mouseP, radius } = this.getOverlapInfo();
    // 半径内にあるアイテムを取得
    const itemsWithinRadius: marietta.webcad.item.MonicaObject[] = allItems.filter((item) => {
      if (!this.isTarget(item)) return false;
      if (!item.itemInfo.getItemPointAry().length) {
        console.log(`${item.itemInfo.id}: 座標取得失敗`);
        return false;
      }
      return this.isPointOverlappingItem(item, mouseP, radius);
    });
    // console.log('リスト', itemsWithinRadius);
    return itemsWithinRadius;
  }

  /**
   * 指定座標が対象アイテムに重なっているか判定
   * 重なりリスト表示、および選択中アイテムを他アイテムのmousedownから保護する判定の両方から使用する
   */
  public isPointOverlappingItem(item: marietta.webcad.item.MonicaObject, mouseP: marietta.core.Point, radius: number): boolean {
    const points = item.itemInfo.getItemPointAry();
    if (!points.length) return false;
    // 点
    if (points.length === 1) return this.checkPointToPoint(points[0], mouseP, radius);
    // 囲み座標
    if (this.checkIncludePoints(points, mouseP)) return true;
    // 囲み内にマウス座標がない、線分と点の関係をチェック
    return this.checkEdgeToPoint(points, mouseP, radius);
  }

  /**
   * 重なっている部屋壁リストを取得
   * @returns 壁リスト
   */
  private getOverlapWallList(): marietta.webcad.item.parts.Wall[] {
    // 複数選択モード中は壁を選択できないようにするため、重なりリストにも壁を含めない
    if (marietta.webcad.WebCADGlobal.webcad2Dmanager.isTapMultiSelectMode) return [];

    const { allItems, mouseP, radius } = this.getOverlapInfo();
    const rm = <marietta.webcad.item.Room[]>allItems.filter((item) => {
      if (!this.isTarget(item)) return false;
      return item.itemInfo.id.includes('rom');
    });
    const walls: marietta.webcad.item.parts.Wall[] = [];
    rm.forEach((room) => walls.push(...this.getWallsWithinRadius(room, mouseP, radius)));
    return walls;
  }

  private isTarget(item: marietta.webcad.item.MonicaObject): boolean {
    if (item instanceof marietta.webcad.item.GroupItem) return false;
    if (item instanceof marietta.webcad.item.parts.GroupItemWall) return false;
    if (this.unnecessaryItem.includes(item.itemInfo.id)) return false;
    if (item.itemInfo.id.includes('str')) return false;
    return true;
  }

  /**
   * 重なり判定に使用する情報を取得
   */
  private getOverlapInfo(): { allItems: marietta.webcad.item.MonicaObject[]; mouseP: marietta.core.Point; radius: number } {
    const tarLayer = marietta.webcad.WebCADGlobal.layerGroup.getFloorLayer(marietta.webcad.WebCADGlobal.webcad2Dmanager.currentFloor) as marietta.webcad.layer.FloorLayer;
    const allItems: marietta.webcad.item.MonicaObject[] = tarLayer.getAllItems();
    const mouseP = new marietta.core.Point(marietta.webcad.WebCADGlobal.layerGroup.spt.mouseX, marietta.webcad.WebCADGlobal.layerGroup.spt.mouseY);
    const radius = marietta.webcad.util.WebCADUtil.convertMMToPix(OVERLAP_HITTEST_RADIUS_MM);
    return { allItems, mouseP, radius };
  }

  /**
   * マウスポイントから半径以内の壁を取得
   * @returns 壁リスト
   */
  private getWallsWithinRadius(room: marietta.webcad.item.Room, mouseP: marietta.core.Point, radius: number): marietta.webcad.item.parts.Wall[] {
    return room.wallObjList.filter((wall) => this.isWallWithinRadius(this.getWallEdge(wall), mouseP, radius));
  }

  private getWallEdge(wall: marietta.webcad.item.parts.Wall) {
    const edge = new marietta3d.AutoGen.utils.RoomEdge();
    edge.CopyPointFrom(wall.globalStartDot, wall.globalEndDot);
    return edge;
  }

  /**
   * 部屋の壁が半径内にあるかチェック
   * @param edge 壁
   */
  private isWallWithinRadius(edge: marietta3d.AutoGen.utils.RoomEdge, mouseP: marietta.core.Point, radius: number) {
    return edge.DistancePtoEdge(mouseP) <= radius;
  }

  /**
   * アイテムの囲み座標内にマウス座標があるかチェック
   */
  private checkIncludePoints(points: marietta3d.AutoGen.utils.RoomPointArray, mouseP: marietta.core.Point): boolean {
    const ret = points.IsInclude(mouseP);
    // 囲み座標内にマウス座標があればtrue(線分も許容)
    if (ret !== marietta3d.AutoGen.utils.RC_INCLUDE.OUTSIDE) return true;
    return false;
  }

  /**
   * 囲み座標を線分にし、線分と点の最短距離をチェック
   */
  private checkEdgeToPoint(points: marietta3d.AutoGen.utils.RoomPointArray, mouseP: marietta.core.Point, radius: number) {
    const edges = points.map((_, i) => points.GetLine(i));
    // すべての辺と点の最短距離をチェック
    const isChecks = edges.map((edge) => edge.DistancePtoEdge(mouseP) <= radius);
    // 1辺でも指定距離以内であればtrue
    return isChecks.some((v) => v);
  }

  /**
   * 点と点の距離が指定の距離以内か
   */
  private checkPointToPoint(point: marietta.core.Point, mouseP: marietta.core.Point, radius: number): boolean {
    const distance = point.distanceTo(mouseP);
    return distance <= radius;
  }

  /**
   * リストをソート
   * ピン、建具、住設、家具、汎用、部屋、床・天井、梁、壁
   */
  private sortList() {
    const tempList = this.itemList.map((v) => v);
    const pins = tempList.filter((v) => v.itemInfo.id === 'txt03_01');
    const fittings = tempList.filter((v) => v.itemInfo.id.includes('fit'));
    const beams = tempList.filter((v) => v.itemInfo.id.includes('bem'));
    const walls = tempList.filter((v) => v.itemInfo.id.includes('wal'));
    const equipments = tempList.filter((v) => v.itemInfo.id.includes('equ'));
    const furnitures = tempList.filter((v) => v.itemInfo.id.includes('fur'));
    const draws = tempList.filter((v) => ['txt01_01', 'txt02_01', 'txt04_01', 'txt04_02', 'txt04_03'].includes(v.itemInfo.id));
    const rooms = tempList.filter((v) => v.itemInfo.id.includes('rom'));
    this.itemList = [];
    this.itemList.push(...pins, ...fittings, ...walls, ...beams, ...equipments, ...furnitures, ...draws, ...rooms);
  }

  /**
   * リスト表表示
   */
  private createList() {
    this.removeList();
    const container = <HTMLElement>document.getElementById('overlapItemListContainer');
    const listBox = <HTMLElement>document.querySelector('#overlapItemListContainer div ul');
    this.addItemToList(listBox);
    this.addWallToList(listBox);
    container.style.display = 'block';
    // リスト表の座標決定
    const off = 25;
    const x = Stage.mouseX + off;
    const y = -(document.querySelector('#webcad2dViewInner').clientHeight - Stage.mouseY) - off;
    container.style.transform = `translate(${x}px,${y}px)`;
  }

  /**
   * 重なりリストにアイテムを追加
   */
  private addItemToList(listBox: HTMLElement) {
    for (const item of this.itemList) {
      if (this.unnecessaryItem.includes(item.itemInfo.id)) {
        continue;
      }
      this.addListElement(listBox, item.itemInfo, false, '', '');
      // 部屋の場合は床と天井も含む
      if (item.itemInfo.id.includes('rom')) {
        this.addListElement(listBox, item.itemInfo, true, '床・天井', this.attachedName);
      }
    }
  }

  /**
   * 重なりリストに壁を追加
   * @param listBox
   */
  private addWallToList(listBox: HTMLElement) {
    for (const item of this.wallItemList) {
      this.addWallListElement(listBox, item);
    }
  }

  /**
   * アイテムをリストに追加
   * @param listBox リスト表HTML要素
   * @param info アイテムデータ
   * @param isAttached 付属情報があるか
   * @param name 付属情報名
   * @param className 付属情報クラス名
   */
  private addListElement(listBox: HTMLElement, info: marietta.webcad.collection.ItemInfoCollection, isAttached: boolean, name: string, className: string) {
    const li = document.createElement('li');
    li.id = info.guid;
    li.classList.add(this.className);
    if (isAttached) li.classList.add(className); // 床・天井の場合はクラス追加
    const reserveName = this.reserveItemName(info);
    li.innerText = !isAttached ? reserveName : `${reserveName} ${name}`;
    listBox.appendChild(li);
  }

  /**
   * 壁をリストに追加
   * @param listBox リスト表HTML要素
   * @param info アイテムデータ
   */
  private addWallListElement(listBox: HTMLElement, wall: marietta.webcad.item.parts.Wall) {
    const li = document.createElement('li');
    li.id = `${wall.parentObjInfo.guid}_wall_${wall.index}`;
    li.classList.add(this.className);
    li.innerText = `${this.reserveRoom(wall.parentObjInfo.id)} 壁`;
    listBox.appendChild(li);
  }

  /**
   * リスト表の内容をクリア
   */
  private removeList() {
    const listBox = <HTMLElement>document.querySelector('#overlapItemListContainer div ul');
    while (listBox.firstChild) {
      listBox.firstChild.removeEventListener(this.isPC ? 'click' : 'touchend', this.handleClick);
      listBox.firstChild.removeEventListener(this.isPC ? 'mouseover' : 'touchstart', this.handleOver);
      if (!this.isPC) {
        listBox.firstChild.removeEventListener('touchmove', this.handleTouchmove);
      }
      listBox.removeChild(listBox.firstChild);
    }
  }

  /**
   * 全アイテム引当
   * @param info アイテムデータ
   * @returns アイテム名
   */
  private reserveItemName(info: marietta.webcad.collection.ItemInfoCollection): string {
    const cloneInfo = info.clone();
    // 部屋でテキストがあるならテキストを返す、それ以外は引当る
    if (cloneInfo.id.includes('rom')) {
      return cloneInfo.text ? cloneInfo.text : this.reserveRoom(cloneInfo.id);
    }
    if (cloneInfo.id === 'fit15_01') cloneInfo.id = 'fit15_09'; // FIX(金)
    // if (cloneInfo.id === 'fur26_01') cloneInfo.id = 'fur28_02'; // 汎用作図立方体
    if (cloneInfo.id.includes('str')) cloneInfo.id = 'str'; // 階段
    return this.reserveItem(cloneInfo.id);
  }

  /**
   * PartsDefに記載のあるアイテム
   */
  private reserveItem(id: string): string {
    const item = marietta.webcad.WebCADGlobal.partsDefObjectData[id];
    if (!item) {
      return this.reserveOther(id);
    }
    switch (id) {
      case 'fit03_09':
        return '片開（金建て）';
      case 'fur09_01':
        return '食器棚';
    }
    const name = marietta.webcad.WebCADGlobal.partsDefObjectData[id].name;
    if (!name) {
      console.log('partsDefに名称が載っていないアイテム：', id);
    }
    return name ?? id;
  }

  /**
   * PartsDefにない、部屋でもないアイテム引当
   */
  private reserveOther(id: string): string {
    console.log(id);
    switch (id) {
      case 'txt02_01':
        return '汎用作図 ピン';
      case 'txt01_01':
        return '汎用作図 文字';
      case 'txt03_01':
        return 'ピン';
      case 'txt04_01':
      case 'txt04_02':
      case 'txt04_03':
        return 'パノラマ';
      // case 'fur28_02':
      //   return '汎用立方体';
      case 'fit01_01':
        return '独立壁';
      case 'fit01_02':
        return '下がり壁';
      case 'fit01_03':
        return '腰壁';
      case 'fit02_01':
        return '開口';
      case 'bem01_01':
        return '梁';
      case 'wal01_01':
        return '壁';
      default:
        console.log('アイテムが引き当てられませんでした：', id);
        return id;
    }
  }

  /** 部屋引当 */
  private reserveRoom(id: string): string {
    switch (id) {
      case 'rom10_01':
        return '洋室';
      case 'rom11_01':
        return '和室';
      case 'rom03_01':
        return '玄関';
      case 'rom01_01':
        return 'ポーチ';
      case 'rom27_01':
        return 'ガレージ';
      case 'rom04_01':
        return 'リビング';
      case 'rom05_01':
        return 'ダイニング';
      case 'rom06_01':
        return 'キッチン';
      case 'rom25_01':
        return 'LD';
      case 'rom26_01':
        return 'LDK';
      case 'rom09_01':
        return '浴室';
      case 'rom08_01':
        return 'トイレ';
      case 'rom07_01':
        return '洗面所';
      case 'rom02_01':
        return '廊下';
      case 'rom12_01':
        return '床の間';
      case 'rom13_01':
        return '広縁';
      case 'rom14_01':
        return '収納';
      case 'rom28_01':
        return '土間';
      case 'rom36_01':
        return 'PS';
      case 'rom37_01':
        return 'MB';
      case 'rom18_01':
        return 'バルコニー';
      case 'rom17_01':
        return '吹き抜け';
      default:
        // rom00_01,rom00_00とその他
        return '未設定部屋';
    }
  }

  /**
   * イベント追加
   */
  private addEvent() {
    const list = document.querySelectorAll('#overlapItemListContainer div ul li');
    for (const item of list) {
      item.addEventListener(this.isPC ? 'mouseover' : 'touchstart', this.handleOver);
      item.addEventListener(this.isPC ? 'click' : 'touchend', this.handleClick);
    }
  }

  /**
   * マウスオーバーイベント
   */
  private handleOver = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    const id = (<HTMLElement>e.target).id;
    // 部屋壁
    if (id.includes('wall')) {
      this.selectWall(id);
      if (!this.isPC) (<HTMLElement>e.target).addEventListener('touchmove', this.handleTouchmove);
      return;
    }
    marietta.webcad.WebCADGlobal.itemManager.currentWallGroup = null;
    const overItem = this.itemList.find((v) => v.itemInfo.guid === id);
    marietta.webcad.WebCADGlobal.itemManager.currentItem = overItem;
    if (!this.isPC) {
      this.selectItemId = overItem.itemInfo.guid; // guid保持
      (<HTMLElement>e.target).addEventListener('touchmove', this.handleTouchmove);
    } else {
      if ((<HTMLElement>e.target).classList.contains(this.attachedName)) {
        marietta.webcad.WebCADGlobal.quotationItemList.highlightQuotationItemList(overItem, 'floor-ceiling');
      }
    }
  };

  /**
   * iPad対応、マウスオーバー
   */
  private handleTouchmove = (e: TouchEvent) => {
    e.preventDefault();
    const list = document.querySelectorAll('#overlapItemListContainer div ul li');
    const touch = e.changedTouches[0];
    const elms = document.elementsFromPoint(touch.clientX, touch.clientY);
    for (const item of elms) {
      if (!item.classList.contains(this.className)) return;
      // ホバー
      list.forEach((v) => v.classList.remove('hovered'));
      item.classList.add('hovered');

      if (item.id.includes('wall')) {
        this.selectWall(item.id);
        return;
      }
      marietta.webcad.WebCADGlobal.itemManager.currentWallGroup = null;
      const overItem = this.itemList.find((v) => v.itemInfo.guid === item.id);
      marietta.webcad.WebCADGlobal.itemManager.currentItem = overItem;
      this.selectItemId = overItem.itemInfo.guid; // guid保持
      if (item.classList.contains(this.attachedName)) {
        // 見積り表ハイライト
        marietta.webcad.WebCADGlobal.quotationItemList.highlightQuotationItemList(overItem, 'floor-ceiling');
        this.isAttachedItem = true;
      } else {
        this.isAttachedItem = false;
      }
    }
  };

  /**
   * リスト選択イベント
   */
  private handleClick = (e: MouseEvent | TouchEvent) => {
    const list = document.querySelectorAll('#overlapItemListContainer div ul li');
    if (!this.isPC) {
      list.forEach((v) => v.classList.remove('hovered'));
      const touch = (<TouchEvent>e).changedTouches[0];
      const touchX = touch.clientX;
      const touchY = touch.clientY;
      const rect = document.getElementById('overlapItemListContainer').getBoundingClientRect();
      if (touchX < rect.left || touchX > rect.right || touchY < rect.top || touchY > rect.bottom) {
        // console.log('リスト外');
        return;
      }
    }
    const id = !this.isPC && this.selectItemId ? this.selectItemId : (<HTMLElement>e.target).id;
    if (id.includes('wall')) {
      this.selectWall(id);
    } else {
      marietta.webcad.WebCADGlobal.itemManager.currentWallGroup = null;
      const overItem = this.itemList.find((v) => v.itemInfo.guid === id);
      marietta.webcad.WebCADGlobal.itemManager.currentItem = overItem;
      if ((<HTMLElement>e.target).classList.contains(this.attachedName) || (!this.isPC && this.isAttachedItem)) {
        marietta.webcad.WebCADGlobal.quotationItemList.highlightQuotationItemList(overItem, 'floor-ceiling');
      }
    }
    this.hideList();
  };

  /**
   * 部屋壁を選択中の状態にする
   */
  private selectWall(id: string) {
    const str = id.split('_');
    const rm = this.itemList.find((v) => v.itemInfo.guid.includes(str[0]));
    if (rm && rm instanceof marietta.webcad.item.Room) {
      marietta.webcad.WebCADGlobal.itemManager.currentItem = null;
      marietta.webcad.WebCADGlobal.itemManager.currentWallGroup = null;
      marietta.webcad.WebCADGlobal.itemManager.addWallToGroupObject(rm.wallObjList[Number(str[2])]);
    }
    if (!this.isPC) this.selectItemId = id; // id保持
  }
}
